<script setup>
import { ref, computed, watch, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import Icon from 'next/icon/Icon.vue';

const props = defineProps({
  label: {
    type: String,
    required: true,
  },
  value: {
    type: [String, Number, Boolean],
    default: null,
  },
  suffix: {
    type: String,
    default: '',
  },
  removable: {
    type: Boolean,
    default: false,
  },
  allowNA: {
    type: Boolean,
    default: false,
  },
  step: {
    type: String,
    default: '1',
  },
  placeholder: {
    type: String,
    default: '',
  },
  icon: {
    type: String,
    default: null,
  },
  /** When bound (with fieldKey), only this field is in edit mode; clicking another closes this one */
  activeEditKey: {
    type: String,
    default: undefined,
  },
  fieldKey: {
    type: String,
    default: null,
  },
});

const emit = defineEmits(['update', 'remove', 'start-edit', 'end-edit']);

const { t } = useI18n();

const inputValue = ref(props.value);

const localEditing = ref(false);

/** Parent controls edit state when activeEditKey is bound (even if currently null). */
const isParentControlled = computed(
  () => props.fieldKey != null && props.activeEditKey !== undefined
);

const isEditing = computed(() => {
  if (isParentControlled.value) {
    return props.activeEditKey === props.fieldKey;
  }
  return localEditing.value;
});

const displayValue = () => {
  if (props.value === 'n/a') return 'N/A';
  if (props.value == null || props.value === '') {
    return props.placeholder;
  }
  if (props.value === true || props.value === 'true') {
    return t('ONBOARDING_WIZARD.YES');
  }
  if (props.value === false || props.value === 'false') {
    return t('ONBOARDING_WIZARD.NO');
  }
  return props.suffix ? `${props.value} ${props.suffix}` : String(props.value);
};

const startEditing = () => {
  inputValue.value = props.value === 'n/a' ? '' : props.value;
  if (isParentControlled.value) {
    emit('start-edit', props.fieldKey);
  } else {
    localEditing.value = true;
  }
};

const saveValue = () => {
  emit('update', inputValue.value);
  if (isParentControlled.value) {
    emit('end-edit');
  } else {
    localEditing.value = false;
  }
};

const cancelEditing = () => {
  inputValue.value = props.value;
  if (isParentControlled.value) {
    emit('end-edit');
  } else {
    localEditing.value = false;
  }
};

const fieldRoot = ref(null);

const handleClickOutside = e => {
  if (fieldRoot.value && !fieldRoot.value.contains(e.target)) {
    saveValue();
  }
};

// Sync inputValue with props.value when not editing
watch(
  () => props.value,
  newValue => {
    if (!isEditing.value) {
      inputValue.value = newValue;
    }
  }
);

watch(isEditing, editing => {
  if (editing) {
    document.addEventListener('mousedown', handleClickOutside);
  } else {
    document.removeEventListener('mousedown', handleClickOutside);
  }
});

onBeforeUnmount(() => {
  document.removeEventListener('mousedown', handleClickOutside);
});

const handleRemove = () => {
  emit('remove');
};

const toggleNA = () => {
  emit('update', props.value === 'n/a' ? null : 'n/a');
};
</script>

<template>
  <div
    ref="fieldRoot"
    class="flex group flex-col gap-1 rounded-lg px-2 py-1.5 transition-colors is-editable hover:bg-n-slate-2 dark:hover:bg-n-solid-1"
  >
    <!-- Label -->
    <div class="flex items-center gap-2 min-w-0">
      <Icon
        v-if="icon"
        :icon="icon"
        class="size-4 opacity-50 text-woot-600 dark:text-woot-200 flex-shrink-0"
      />
      <p
        class="font-medium text-sm text-slate-900 dark:text-white truncate !m-0"
      >
        {{ label }}
      </p>
    </div>

    <!-- Value/Input + actions -->
    <div class="flex items-center gap-1 min-w-0 w-full h-9">
      <div class="min-w-0 flex-1 h-9">
        <input
          v-if="isEditing"
          v-model="inputValue"
          type="number"
          :step="step"
          min="0"
          autofocus
          class="reset-base no-margin box-border w-full h-9 text-sm border border-n-brand rounded px-2 focus:outline-none focus:ring-1 focus:ring-n-brand/50 bg-n-background text-n-slate-12 !mb-0 dark:!bg-neutral-950"
          @keydown.enter.prevent="saveValue"
          @keyup.escape="cancelEditing"
          @blur="saveValue"
        />
        <button
          v-else
          type="button"
          class="box-border w-full h-9 text-left px-2 text-sm rounded border border-transparent hover:bg-n-slate-2 dark:hover:bg-n-solid-2 transition-colors"
          :class="{
            'text-n-slate-9 italic': value === 'n/a',
            'text-n-slate-11': value !== 'n/a' && !value && value !== 0,
            'text-n-slate-12': value !== 'n/a' && (value || value === 0),
          }"
          @click="startEditing"
        >
          {{ displayValue() }}
        </button>
      </div>
      <div
        class="flex-shrink-0 flex items-center gap-0.5 h-9 transition-opacity"
        :class="
          isEditing
            ? 'invisible'
            : 'opacity-0 group-hover:opacity-100'
        "
      >
        <button
          type="button"
          class="flex p-1 text-n-slate-11 hover:text-n-brand rounded transition-colors w-7 h-7 justify-center items-center"
          :title="t('CUSTOM_ATTRIBUTES.ACTIONS.EDIT')"
          :tabindex="isEditing ? -1 : 0"
          @click="startEditing"
        >
          <Icon icon="i-lucide-pen-line" class="w-3.5 h-3.5" />
        </button>
        <button
          v-if="removable || allowNA"
          type="button"
          class="flex px-1 text-xs font-medium rounded transition-colors h-7 justify-center items-center"
          :class="
            value === 'n/a'
              ? 'text-n-slate-9 hover:text-n-slate-12 line-through'
              : 'text-n-slate-11 hover:text-amber-600'
          "
          title="N/A"
          :tabindex="isEditing ? -1 : 0"
          @click="toggleNA"
        >
          N/A
        </button>
        <button
          v-if="removable"
          type="button"
          class="flex p-1 text-n-slate-11 hover:text-n-ruby-9 rounded transition-colors w-7 h-7 justify-center items-center"
          :title="t('CUSTOM_ATTRIBUTES.ACTIONS.DELETE')"
          :tabindex="isEditing ? -1 : 0"
          @click="handleRemove"
        >
          <Icon icon="i-lucide-trash-2" class="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  </div>
</template>
